import { StyleSheet, View } from 'react-native'
import { StackScreenProps } from '@react-navigation/stack'
import { AppNavigatorParams } from '../../navigation/types'
import { useTranslation } from 'react-i18next'
import {
  useUpdateNodeMutation,
  useCreateNodeMutation,
  useDeleteNodeMutation,
  useMyNodeByIdQuery,
  MyNodeByIdQuery,
  useGetNodeTiersQuery,
  useExpireNodeTierForTestingMutation,
} from '../../generated/graphql'
import { createStyle } from '../../contexts/global-styles.provider'
import { useEffect, useMemo, useRef, useState } from 'react'
import Toast from 'react-native-root-toast'
import { ScreenWrapper } from '../../@ui/containers/grid/screen.wrapper'
import { Row } from '../../@ui'
import { Title, TextInput, Button, Text } from 'react-native-paper'
import { LabeledSwitch } from '../../@ui/form/labeled.switch'
import { useTheme } from '../../hooks/theme.hook'
import { MessageType } from '../../utils/message-type'
import * as Sentry from '@sentry/react-native'
import {
  ConfirmDialogue,
  ConfirmDialogueRef,
} from '../../@ui/containers/modals/confirm.dialogue'
import { TouchableIconWithToolTip } from '../../utils/meldd-tooltip'
import { Icon } from 'react-native-paper'
import { CreateInvitationButton } from './invitation-links/invitation/create-invitation.button'
import { useNavigation } from '@react-navigation/native'
import { AppNavigation } from '../../navigation/types'
import { TransferNodeOwnershipModal } from './components/transfer-node-ownership.modal'
import { NodeTierList } from './payment/node-tier.cards'
import { useHandleTierAction } from './payment/handle-tier.hook'
import { PaymentChannelModal } from './payment/payment-channel.modal'

const PrivateActionButtons = ({
  node,
  handleUpdateNode,
}: {
  node: MyNodeByIdQuery['nodeById']
  handleUpdateNode: () => void
}) => {
  const { t } = useTranslation('nodes')
  const navigation = useNavigation<AppNavigation>()
  const styles = useStyle()

  if (!node.isPrivate) {
    return null
  }
  return (
    <>
      <Button
        mode="outlined"
        icon="account-cog"
        onPress={async () => {
          // If is private but not saved
          if (!node.isPrivate) {
            await handleUpdateNode()
          }
          navigation.navigate('NodeMembers', { nodeId: node.id })
        }}
        style={styles.actionButton}
      >
        {t('manage-members')}
      </Button>

      <Button
        mode="outlined"
        icon="link-variant"
        onPress={async () => {
          // If is private but not saved
          if (!node.isPrivate) {
            await handleUpdateNode()
          }
          navigation.navigate('InvitationLinks', { nodeId: node.id })
        }}
        style={styles.actionButton}
      >
        {t('manage-links')}
      </Button>
      <CreateInvitationButton nodeId={node.id} iconOnly={false} />
    </>
  )
}
function NodeSettingsScreen({
  route,
  navigation,
}: StackScreenProps<AppNavigatorParams, 'NodeSettings'>) {
  const {
    nodeId,
    isPrivate: initialIsPrivate,
    seats: initialSeats,
  } = route.params
  const { t } = useTranslation('nodes')
  const theme = useTheme()
  const { data, loading, refetch } = useMyNodeByIdQuery({
    variables: { id: nodeId },
    skip: !nodeId,
  })
  const node = data?.nodeById

  // Fetch node tiers to find pending downgrade
  const { data: nodeTiersData, refetch: refetchNodeTiers } =
    useGetNodeTiersQuery({
      skip: !nodeId,
      variables: {
        filter: {
          nodeId: nodeId,
          status: 'pending_renovation',
        },
      },
    })

  // Find pending tier for the same tier family.
  const pendingTier = useMemo(() => {
    if (!nodeTiersData?.nodeTiers?.nodeTiers || !node?.activeTier || !nodeId) {
      return undefined
    }
    const activeTier = node.activeTier
    return nodeTiersData.nodeTiers.nodeTiers.find(
      (tier) => tier.name === activeTier.name
    )
  }, [nodeTiersData, node?.activeTier, nodeId])
  const styles = useStyle()
  const [createNode, { loading: isCreatingNode }] = useCreateNodeMutation()
  const [updateNode, { loading: isUpdatingNode }] = useUpdateNodeMutation()
  const [deleteNode, { loading: isDeletingNode }] = useDeleteNodeMutation()
  const confirmDialogRef = useRef<ConfirmDialogueRef>(null)
  const [isTransferModalVisible, setIsTransferModalVisible] = useState(false)
  const isSaving = isCreatingNode || isUpdatingNode

  const handleDeleteNode = async () => {
    if (!node) {
      return
    }

    try {
      await deleteNode({
        variables: { id: node.id },
      })

      Toast.show(t('node-deleted-success'), MessageType.info)
      navigation.replace('MyNodes')
      return true
    } catch (e) {
      Toast.show((e as Error).message, MessageType.error)
      console.error(e)
      Sentry.captureException(e)
      return false
    }
  }

  const handleTransferOwnership = () => {
    if (!node) {
      return
    }

    if (node.isPrivate) {
      Toast.show(t('transfer-ownership-from-members-menu'), {
        position: Toast.positions.TOP,
      })
      navigation.navigate('NodeMembers', { nodeId: node.id })
      return
    }

    setIsTransferModalVisible(true)
  }

  const [expireNodeTierForTesting] = useExpireNodeTierForTestingMutation()

  const handleExpireTier = async () => {
    if (!node?.activeTier) {
      return
    }

    try {
      await expireNodeTierForTesting({
        variables: { tierId: node.activeTier.id },
      })
      Toast.show(t('tier-expired-success'), MessageType.info)
      refetchNodeTiers()
      window.location.reload()
    } catch (e) {
      Toast.show((e as Error).message, MessageType.error)
      console.error(e)
      Sentry.captureException(e)
    }
  }

  // Form state
  const [name, setName] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [isPrivate, setIsPrivate] = useState<boolean>(initialIsPrivate ?? false)
  const [allowExtraSeats, setAllowExtraSeats] = useState<boolean>(true)
  const [preselectedSeats, setPreselectedSeats] = useState<number | undefined>(
    initialSeats
  )

  // Tier

  // Handle tier action
  const { handleAction, billingInput, clearBilling, setBilling } =
    useHandleTierAction({
      name,
      description,
      isPrivate,
      allowExtraSeats,
    })

  // Determine if we're creating a new node or editing existing one
  const isEditing = !!nodeId
  const isCreating = !nodeId
  const canManageBilling = isCreating || node?.isOwner === true

  useEffect(() => {
    if (loading) return

    // Only check permissions and node existence if we're editing
    if (isEditing) {
      if (!node) {
        navigation.goBack()
        Toast.show(t('no-node-selected'), {
          duration: Toast.durations.LONG,
          position: Toast.positions.CENTER,
        })
        return
      }

      if (!node?.isAdmin) {
        navigation.goBack()
        Toast.show(t('node-settings-not-authorized'), {
          duration: Toast.durations.LONG,
          position: Toast.positions.CENTER,
        })
        return
      }
    }
  }, [node, loading, nodeId, isEditing])

  // Initialize form with current node data (only when editing)
  useEffect(() => {
    if (!node || !isEditing) return
    if (!name) {
      setName(node.name || '')
      setDescription(node.description || '')
      setIsPrivate(node.isPrivate || false)
      setAllowExtraSeats(node.allowExtraSeats ?? true)
    }
  }, [node, isEditing])

  // Initialize form with preselected values (only when creating)
  useEffect(() => {
    if (isCreating && initialIsPrivate !== undefined) {
      setIsPrivate(initialIsPrivate)
    }
  }, [isCreating, initialIsPrivate])

  const handleCreateNode = async () => {
    console.log('handleCreateNode', name, description, isPrivate)
    await createNode({
      variables: {
        input: {
          name,
          description,
          isPrivate,
          allowExtraSeats,
        },
      },
    })
    navigation.replace('MyNodes')

    Toast.show(t('Node created'), MessageType.info)
  }
  const handleUpdateNode = async () => {
    if (!node) {
      return
    }
    await updateNode({
      variables: {
        input: {
          id: node.id,
          name,
          description,
          allowExtraSeats,
        },
      },
    })

    navigation.replace('MyNodes')

    Toast.show(t('Node updated'), MessageType.info)
  }

  const handleSaveNode = async () => {
    try {
      if (isCreating) {
        await handleCreateNode()
        return
      }
      await handleUpdateNode()
    } catch (e) {
      Toast.show((e as Error).message, MessageType.error)
      console.error(e)
      Sentry.captureException(e)
    }
  }

  const containerStyles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          flexDirection: 'column',
          padding: 20,
          alignItems: 'stretch',
          rowGap: theme.spacing(4),
        },
      }),
    [theme]
  )

  if (loading && isEditing)
    return (
      <View style={styles.fullPage}>
        <Text>{t('Loading')}</Text>
      </View>
    )

  return (
    <ScreenWrapper contentContainerStyle={containerStyles.container}>
      <Row style={{ alignItems: 'center' }} columnGap={4}>
        <Title style={{ marginRight: 5 }}>
          {isCreating
            ? t('Create New Node')
            : t('Node Settings for') + ' ' + node?.name}
        </Title>
        {isEditing && node && (
          <TouchableIconWithToolTip
            icon={<Icon source="eye" size={16}></Icon>}
            tooltip={t('view-node')}
            mode="text"
            onPress={() => {
              navigation.navigate('ClusterList', { nodeId: node.id })
            }}
          />
        )}
      </Row>

      {/* Actions Section */}
      {isEditing && node && (
        <View style={styles.actionsSection}>
          <Text style={styles.sectionTitle}>{t('actions')}</Text>
          <View style={styles.actionsContainer}>
            <PrivateActionButtons
              node={node}
              handleUpdateNode={handleUpdateNode}
            />
            <Button
              mode="outlined"
              icon="chart-bar"
              onPress={() =>
                navigation.navigate('NodeStatsAdmin', { nodeId: node.id })
              }
              style={styles.actionButton}
            >
              {t('node-stats')}
            </Button>
          </View>
        </View>
      )}

      <TextInput
        label={t('Node Name')}
        onChangeText={(text) => setName(text)}
        value={name}
        clearButtonMode={'while-editing'}
        mode={'outlined'}
      />

      <TextInput
        label={t('Description')}
        onChangeText={(text) => setDescription(text)}
        value={description}
        clearButtonMode={'while-editing'}
        mode={'outlined'}
        multiline
        numberOfLines={3}
      />

      <Row style={{ justifyContent: 'space-between' }}>
        <Text>{t('visibility')}</Text>
        <LabeledSwitch
          style={{ alignSelf: 'flex-end' }}
          wrapperStyle={{ alignSelf: 'flex-end', columnGap: theme.spacing(4) }}
          label={isPrivate ? t('private') : t('public')}
          value={isPrivate}
          disabled={isEditing}
          icon={isPrivate ? 'lock' : 'earth'}
          onValueChange={setIsPrivate}
        />
      </Row>

      <Row style={{ justifyContent: 'space-between' }}>
        <Text>{t('allow-extra-seats')}</Text>
        <LabeledSwitch
          style={{ alignSelf: 'flex-end' }}
          wrapperStyle={{ alignSelf: 'flex-end', columnGap: theme.spacing(4) }}
          label={allowExtraSeats ? t('enabled') : t('disabled')}
          value={allowExtraSeats}
          icon={
            allowExtraSeats
              ? 'account-multiple-plus'
              : 'account-multiple-remove'
          }
          onValueChange={setAllowExtraSeats}
        />
      </Row>

      {/* Save Button */}
      {!isCreating && (
        <Row style={{ justifyContent: 'flex-end' }}>
          <Button
            onPress={handleSaveNode}
            mode="contained"
            disabled={!name || isDeletingNode}
            loading={isSaving}
          >
            {t('Update')}
          </Button>
        </Row>
      )}

      <NodeTierList
        currentTier={node?.activeTier || undefined}
        pendingTier={pendingTier || undefined}
        visibility={isPrivate ? 'private' : 'public'}
        currentActiveUsers={node?.activeTier?.currentActiveUsers ?? null}
        allowTierChanges={canManageBilling}
        allowExtraSeats={allowExtraSeats}
        action={(action, data) => {
          handleAction(action, {
            ...data,
            nodeTierId: node?.activeTier?.id,
          })
        }}
        onCancelSuccess={async () => {
          // Refetch node data and pending tiers after cancellation/downgrade
          await Promise.all([refetch(), refetchNodeTiers()])
        }}
        onUpgradeSuccess={(billingId, amount, basePrice) => {
          // Set billing input to open payment modal
          setBilling(billingId, amount, basePrice || 0)
        }}
      />
      {/* Delete Button */}
      {isEditing && canManageBilling && (
        <>
          <Text variant="titleMedium">{t('danger-node-settings')}</Text>

          <Row columnGap={theme.spacing(1)} style={{ overflow: 'scroll' }}>
            <Button
              mode="outlined"
              textColor={theme.colors.error}
              icon="delete"
              onPress={() => confirmDialogRef.current?.open()}
              disabled={isDeletingNode}
            >
              {t('Delete Node')}
            </Button>
            <Button
              mode="outlined"
              icon="account-arrow-right"
              onPress={handleTransferOwnership}
              style={styles.actionButton}
            >
              {t('transfer-ownership')}
            </Button>
            {(__DEV__ || process.env.NODE_ENV !== 'production') &&
              node?.activeTier && (
                <Button
                  mode="outlined"
                  textColor={theme.colors.error}
                  icon="clock-alert"
                  onPress={handleExpireTier}
                >
                  {t('expire-tier-testing')}
                </Button>
              )}
          </Row>
        </>
      )}

      <PaymentChannelModal
        onDismiss={() => clearBilling()}
        allowCoupon={isCreating}
        billing={billingInput || undefined}
      />

      <ConfirmDialogue
        ref={confirmDialogRef}
        label={t('delete-confirm-message')}
        acceptLabel={t('delete')}
        cancelLabel={t('cancel')}
        onContinue={async () => {
          await handleDeleteNode()
          return true
        }}
      />

      {node && (
        <TransferNodeOwnershipModal
          visible={isTransferModalVisible}
          onDismiss={() => setIsTransferModalVisible(false)}
          node={node}
        />
      )}
    </ScreenWrapper>
  )
}

export default NodeSettingsScreen

const useStyle = createStyle(({ theme }) =>
  StyleSheet.create({
    fullPage: {
      height: '100%',
      justifyContent: 'center',
      alignItems: 'center',
    },
    actionsSection: {
      marginBottom: theme.spacing(2),
    },
    sectionTitle: {
      fontSize: 16,
      fontWeight: '600',
      marginBottom: theme.spacing(2),
      color: theme.colors.onSurface,
    },
    actionsContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: theme.spacing(2),
      justifyContent: 'flex-start',
    },
    actionButton: {
      flex: 1,
      minWidth: 120,
      maxWidth: 200,
    },
    dangerArea: {
      marginTop: theme.spacing(1),
    },
  })
)
